/*!
 * Copyright (c) REWE Digital GmbH
 */
import { Host, h } from "@stencil/core";
import { MDCTabBar } from "@material/tab-bar";
/**
 * The vx-tabs component.
 */
export class VxTabs {
    constructor() {
        this.data = [];
        this.initialTab = undefined;
        this.active = 0;
        this.minTabCount = 3;
    }
    /**
     * Toggle function called by click event on .toggle.
     *
     * @param {{}} event - The click event.
     * @param {{}} event.detail - The click event.
     * @param {number} event.detail.number - The click event.
     */
    toggle(event) {
        const newIndex = event.detail.index;
        if (this.active !== newIndex) {
            if (this.active !== -1) {
                this.close();
            }
            this.open(newIndex);
            this.active = newIndex;
        }
        this.tabSelected.emit({
            index: newIndex,
        });
    }
    /**
     * Filter tabs data for empty content/title values.
     * If minTabCount > actual tab count, adds empty tabs to list.
     */
    filterContent() {
        const data = this.data.filter(item => item.content !== '' && item.title !== '');
        if (this.minTabCount > -1 && data.length < this.minTabCount) {
            const diff = this.minTabCount - data.length;
            for (let i = 0; i < diff; i++) {
                data.push({
                    title: '',
                    content: '',
                });
            }
        }
        return data;
    }
    /**
     * Lifecycle hook called before component is loaded. Called once per component.
     */
    async componentWillLoad() {
        if (this.initialTab)
            this.toggle({
                detail: {
                    index: this.initialTab,
                },
            });
    }
    /**
     * Lifecycle hook called after component is loaded.
     */
    async componentDidLoad() {
        this.tabBarContent.removeAttribute('hidden');
        this.tabBar.removeAttribute('hidden');
        const mdcTabBar = new MDCTabBar(this.tabBar);
        mdcTabBar.listen('MDCTabBar:activated', this.toggle.bind(this));
    }
    /**
     * Open the content with given index number.
     *
     * @param {number} index - The index to open.
     */
    open(index) {
        // todo use class directives
        this.host.shadowRoot.querySelector('.tab-bar-content[data-index="' + index + '"]').classList.add('tab-bar-content--active');
        this.host.shadowRoot.querySelector('.mdc-tab[data-index="' + index + '"]').classList.add('mdc-tab--active');
        this.host.shadowRoot.querySelector('.mdc-tab[data-index="' + index + '"] .mdc-tab-indicator').classList.add('mdc-tab-indicator--active');
    }
    /**
     * Close the currently displayed content.
     */
    close() {
        // todo use class directives
        this.host.shadowRoot.querySelector('.tab-bar-content[data-index="' + this.active + '"]').classList.remove('tab-bar-content--active');
        const button = this.host.shadowRoot.querySelector('.mdc-tab[data-index="' + this.active + '"]');
        button.classList.remove('mdc-tab--active');
        button.setAttribute('tabindex', '0');
        this.host.shadowRoot.querySelector('.mdc-tab[data-index="' + this.active + '"] .mdc-tab-indicator').classList.remove('mdc-tab-indicator--active');
        this.active = -1;
    }
    /**
     * Renders the component.
     *
     * @returns {JSX.Element} The rendered component.
     */
    render() {
        var _a, _b;
        return (h(Host, { key: 'b89ff86028cb0d956e19745cd634db7c83f6e817' }, h("div", { key: 'd6e90d340bf002c7e7688b977d50d1126e826dd8', ref: (el) => { this.tabBar = el; }, class: "mdc-tab-bar", role: "tablist" }, h("div", { key: '363fc5626af04bf111e9d17ef4eee54199385084', class: "mdc-tab-scroller" }, h("div", { key: '6abf461b5b60239124fc94742d53c5f1d5cbd708', class: "mdc-tab-scroller__scroll-area" }, h("div", { key: '4e4dcc086accedd1b8c88b3aff47e1094f649c43', class: "mdc-tab-scroller__scroll-content" }, (_a = this.filterContent()) === null || _a === void 0 ? void 0 : _a.map((row, index) => ((() => {
            return (h("button", { "data-index": index, class: {
                    'mdc-tab': true,
                    'mdc-tab--active': index === this.active,
                    'disabled': row.title === '',
                }, role: "tab", tabindex: "0", disabled: row.title === '' }, h("span", { class: "mdc-tab__content" }, h("slot", { name: 'tabs-toggle-' + index }, h("span", { class: "mdc-tab__text-label" }, row.title))), h("span", { class: {
                    'mdc-tab-indicator': true,
                    'mdc-tab-indicator--active': index === this.active,
                } }, h("span", { class: "mdc-tab-indicator__content\n                                                        mdc-tab-indicator__content--underline" })), h("span", { class: "mdc-tab__ripple" })));
        })())))))), h("div", { key: 'bbe14d1102c3bd1ef8be62b52c4795d28f2f8f43', class: "tab-bar-contents", ref: (el) => { this.tabBarContent = el; } }, (_b = this.filterContent()) === null || _b === void 0 ? void 0 : _b.map((row, index) => {
            var _a;
            return (h("div", { class: { 'tab-bar-content': true, 'tab-bar-content--active': index === this.active }, "data-index": index, part: (_a = row.part) !== null && _a !== void 0 ? _a : 'content' }, h("slot", { name: 'tabs-content-pre-' + index }), h("slot", { name: 'tabs-content-' + index }, h("div", { innerHTML: row.content }))));
        }))));
    }
    static get is() { return "vx-tabs"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-tabs.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-tabs.css"]
        };
    }
    static get properties() {
        return {
            "data": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "{\n        title: string,\n        content: string,\n        part?: string,\n    }[]",
                    "resolved": "{ title: string; content: string; part?: string; }[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Content to display."
                },
                "defaultValue": "[]"
            },
            "initialTab": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Initial tab index."
                },
                "attribute": "initial-tab",
                "reflect": false
            },
            "active": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Currently active tab index."
                },
                "attribute": "active",
                "reflect": false,
                "defaultValue": "0"
            },
            "minTabCount": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Min tab display count.\n-1 displays actual tabs count."
                },
                "attribute": "min-tab-count",
                "reflect": false,
                "defaultValue": "3"
            }
        };
    }
    static get events() {
        return [{
                "method": "tabSelected",
                "name": "tabSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "{index: number}",
                    "resolved": "{ index: number; }",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "host"; }
}
//# sourceMappingURL=vx-tabs.js.map
